import moment from 'app/lib/moment/moment';

/*globals moment*/
/**
 * 99designs monkey patch for moment.js
 * Overrides the relativeTime and substituteTimeAgo functions in moment.js
 * This allows for more precise dates with two figures such as;
 *      1 year X months
 *      2 hours 1 min
 *      2 days 2 hours
 * Currently the following scenarios are not supported;
 *      1 month, 1 day
 *      1 month, X days
 *      X months, 1 day
 *      X months, X days
 *      1 min, 1 second
 *      1 min, X seconds
 *      X mins, 1 second
 *      X mins, X seconds
 * See https://github.com/99designs/bugs/issues/621
 */

/**
 * Helper function for moment.fn.from, moment.fn.fromNow, and moment.duration.fn.humanize
 * @globals moment.relativeTime.shortFormat and moment.relativeTime.fullFormat
 *      These are loaded from appropriate moment lang files eg ./lang/en.js
 *
 * @param string string representing the format of the string to return
 *      eg "yMM" (1 year X months) or "hmm" (1 hour X minutes)
 * @param number a single number or array of numbers used to be substitued into a given format
 * @param withoutSuffix boolean
 * @param isFuture boolean
 * @param shortFormat boolean True If the output should be as small as possible eg "2m" instead of "2 mins"
 *      examples:
 *      substituteTimeAgo("hmm", [1, 30], false, false)       -> "1 hour, 30 mins"
 *      substituteTimeAgo("hmm", [1, 30], false, false, true) -> "1h, 30m"
 *      substituteTimeAgo("M",         1, false, false)       -> "a month"
 * @returns string A user friendly formated time
 **/
function substituteTimeAgo(string, number, withoutSuffix, isFuture, shortFormat) {
    var formats = shortFormat ?
        moment.relativeTime.shortFormat :
        moment.relativeTime.fullFormat;

    var rt = formats[string];

    // Handle single number strings eg. X days (number is a single value)
    if (typeof number === 'number') {
        return (typeof rt === 'function') ?
            rt(number || 1, !!withoutSuffix, string, isFuture) :
            rt.replace(/%d/i, number || 1);
        // Number is an array, Handle strings with multiple numbers eg. X days, X hours
    } else {
        var i = 0, result = '';
        return rt.replace(/%d/g, function() {
            result = number[i];
            i++;
            return result;
        });
    }
}

/**
 * @param milliseconds int The time in milliseconds
 * example:
 *    relativeTime(-199210862, false, false) -> "2 days, 7 hours"
 *    relativeTime(-43851418749, false, false) -> "1 year 5 months"
 *    relativeTime(-772432680, false, false) -> "9 days"
 *    relativeTime(-30638170038, false, false) -> "a year"
 * @returns string A user friendly formated time
 **/
function relativeTime(milliseconds, withoutSuffix, shortFormat) {
    var roundedSeconds = Math.round(Math.abs(milliseconds) / 1000),
        roundedMinutes = Math.round(roundedSeconds / 60),

        hours = Math.floor(roundedMinutes / 60),
        hourMins = roundedMinutes % 60,
        roundedHours = Math.round(roundedMinutes / 60),

        days = Math.floor(roundedHours / 24),
        dayHours = roundedHours % 24,
        roundedDays = Math.round(roundedHours / 24),

        roundedMonths = Math.round(roundedDays / 30),

        years = Math.floor(roundedMonths / 12),
        yearMonths = roundedMonths % 12,
        roundedYears = Math.round(roundedMonths / 12);

    var args = roundedSeconds <= 120 && ['s', roundedSeconds] ||
            // X mins
            roundedMinutes < 60 && ['mm', roundedMinutes] ||
            // 1 hour 1 min, 1 hour X mins, 1 hour
            hours === 1 && hourMins == 1 && ['hm', [hours, hourMins]] ||
            hours === 1 && hourMins > 1 && ['hmm', [hours, hourMins]] ||
            hours === 1 && ['h', roundedHours] ||
            // 2-12 hours 1 min, 2-12 hours X mins, 13+ hours
            hours <= 12 && hourMins === 1 && ['hhm', [hours, hourMins]] ||
            hours <= 12 && hourMins > 1 && ['hhmm', [hours, hourMins]] ||
            hours < 24 && ['hh', roundedHours] ||
            // 1 day 1 hour, 1 day X hours, 1 day
            days === 1 && dayHours === 1 && ['dh', [days, dayHours]] ||
            days === 1 && dayHours > 1 && ['dhh', [days, dayHours]] ||
            days === 1 && ['d', roundedDays] ||
            // 2-3 days 1 hour, 2-3 days X hours, 4+ days
            days <= 3 && dayHours === 1 && ['ddh', [days, dayHours]] ||
            days <= 3 && dayHours > 1 && ['ddhh', [days, dayHours]] ||
            days <= 60 && ['dd', roundedDays] ||
            // X months
            years == 0 && yearMonths == 1 && ['M', yearMonths] ||
            years == 0 && yearMonths <= 11 && ['MM', yearMonths] ||
            // 1 year 1 month, 1 year X months, 1 year
            years === 1 && yearMonths === 1 && ['yM', [years, yearMonths]] ||
            years === 1 && yearMonths > 1 && ['yMM', [years, yearMonths]] ||
            years === 1 && ['y', roundedYears] ||
            // 2-3 years 1 month, 2-3 years X months, 4+ years
            years <= 3 && yearMonths === 1 && ['yyM', [years, yearMonths]] ||
            years <= 3 && yearMonths > 1 && ['yyMM', [years, yearMonths]] ||
            ['yy', roundedYears]
        ;

    args[2] = withoutSuffix;
    args[3] = milliseconds > 0;
    args[4] = shortFormat;
    return substituteTimeAgo.apply({}, args);
}

moment.duration.fn.humanize = function (withSuffix, shortFormat) {
    var difference = +this,
        rel = shortFormat ? moment.relativeTime.shortFormat : moment.relativeTime.fullFormat;

    if (!rel) {
        rel = moment.relativeTime;
    }

    var output = relativeTime(difference, !withSuffix, shortFormat);

    if (withSuffix) {
        output = (difference <= 0 ? rel.past : rel.future).replace(/%s/i, output);
    }

    return output;
};

moment.fn.from = function (time, withoutSuffix, shortFormat) {
    return moment.duration(this.diff(time)).humanize(!withoutSuffix, shortFormat);
};

moment.fn.fromNow = function (withoutSuffix, shortFormat) {
    return this.from(moment(), withoutSuffix, shortFormat);
};

export default moment;



// WEBPACK FOOTER //
// ./src/web/js/app/lib/moment/moment-99designs.js